package org.example.project.screens_net

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardTheme
import org.example.project.enginekartei.KarteiApi
import org.example.project.service.LoginService
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text

val netstandortverwaltungKarteiPages = emptyList<All>()

private val standortAddressFields = listOf(
    "standort" to "Standort",
    "strasse" to "Straße",
    "hausnummer" to "Hausnummer",
    "plz" to "PLZ",
    "ort" to "Ort",
    "land" to "Land"
)

private val standortContactFields = listOf(
    "telefon" to "Telefon",
    "email" to "E-Mail",
    "webseite" to "Webseite"
)

@Composable
fun NETStandortverwaltung() {
    val scope = rememberCoroutineScope()
    var firmenId by remember { mutableStateOf("") }
    var firmenPosition by remember { mutableStateOf("") }
    var form by remember { mutableStateOf(newStandort("")) }
    var loading by remember { mutableStateOf(true) }
    var saving by remember { mutableStateOf(false) }
    var message by remember { mutableStateOf("") }

    suspend fun reload() {
        loading = true
        firmenId = LoginService.getUser()?.firmenId.orEmpty().trim()
        val company = if (firmenId.isBlank()) null else runCatching { KarteiApi.loadUnternehmen(firmenId) }.getOrNull()
        firmenPosition = company?.position.orEmpty()
        form = company?.toNetStandortForm(firmenId) ?: newStandort(firmenId)
        loading = false
    }

    fun updateText(field: String, value: String) {
        form = when (field) {
            "standort" -> form.copy(standort = value)
            "strasse" -> form.copy(strasse = value)
            "hausnummer" -> form.copy(hausnummer = value)
            "plz" -> form.copy(plz = value)
            "ort" -> form.copy(ort = value)
            "land" -> form.copy(land = value)
            "telefon" -> form.copy(telefon = value)
            "email" -> form.copy(email = value)
            "webseite" -> form.copy(webseite = value)
            else -> form
        }
    }

    fun save() {
        if (saving) return
        saving = true
        message = ""
        scope.launch {
            val state = form.copy(
                firmenId = form.firmenId.ifBlank { firmenId },
                land = form.land.ifBlank { "DE" },
                firma = form.standort.ifBlank { form.firma },
                kunde = form.standort.ifBlank { form.kunde.ifBlank { form.firma } },
                webseite = form.webseite,
                gegruendetAm = ""
            )
            val saved = if (firmenId.isBlank()) null else runCatching { KarteiApi.saveUnternehmen(state, firmenId) }.getOrNull()
            if (saved == null) {
                message = "Netzwerkstandort konnte nicht gespeichert werden."
            } else {
                message = "Netzwerkstandort gespeichert."
                reload()
            }
            saving = false
        }
    }

    LaunchedEffect(Unit) { reload() }

    Style(StandortverwaltungStyles)
    Div({ classes(StandortverwaltungStyles.page) }) {
        Div({ classes(StandortverwaltungStyles.shell) }) {
            Div({ classes(StandortverwaltungStyles.topbar) }) {
                Div({ classes(StandortverwaltungStyles.header) }) {
                    Div({ classes(StandortverwaltungStyles.iconBox) }) {
                        Img(src = "images/domain.svg", attrs = { classes(StandortverwaltungStyles.icon) })
                    }
                    Div({ classes(StandortverwaltungStyles.headerText) }) {
                        Div({ classes(StandortverwaltungStyles.titleRow) }) {
                            H3({ classes(StandortverwaltungStyles.title) }) {
                                Text(form.standort.ifBlank { "Standortverwaltung" })
                                if (firmenPosition.isNotBlank()) {
                                    Span({ classes(StandortverwaltungStyles.networkPosition) }) {
                                        Text(" - $firmenPosition")
                                    }
                                }
                            }
                        }
                    }
                }
                Div({ classes(StandortverwaltungStyles.actions) }) {
                    LumaticaButton(
                        label = if (saving) "Speichern..." else "Speichern",
                        onClick = ::save,
                        variant = LumaticaButtonVariant.Action,
                        enabled = !saving,
                        heightPx = 36
                    )
                }
            }

            if (message.isNotBlank()) {
                Span({ classes(StandortverwaltungStyles.message) }) { Text(message) }
            }

            Div({ classes(StandortverwaltungStyles.content) }) {
                Div({ classes(StandortverwaltungStyles.surface) }) {
                    StandortFormSection(
                        title = "Adresse",
                        groups = listOf("Standort" to standortAddressFields),
                        form = form,
                        onChange = ::updateText
                    )
                    StandortFormSection(
                        title = "Kontakt",
                        groups = listOf("Kommunikation" to standortContactFields),
                        form = form,
                        onChange = ::updateText
                    )
                }
            }
        }
    }
}

@Composable
private fun StandortFormSection(
    title: String,
    groups: List<Pair<String, List<Pair<String, String>>>>,
    form: All,
    onChange: (String, String) -> Unit
) {
    Div({ classes(StandortverwaltungStyles.sectionBlock) }) {
        Div({ classes(StandortverwaltungStyles.sectionTitle) }) { Text(title) }
        groups.forEach { (groupTitle, fields) ->
            Div({ classes(StandortverwaltungStyles.groupBlock) }) {
                Div({ classes(StandortverwaltungStyles.groupTitle) }) { Text(groupTitle) }
                Div({ classes(StandortverwaltungStyles.fieldGrid) }) {
                    fields.forEach { (field, label) ->
                        Div({ classes(StandortverwaltungStyles.fieldRow) }) {
                            Span({ classes(StandortverwaltungStyles.fieldLabel) }) { Text(label) }
                            StandortTextInput(
                                value = standortFieldValue(form, field),
                                onChange = { onChange(field, it) }
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun StandortTextInput(value: String, onChange: (String) -> Unit) {
    Input(InputType.Text, attrs = {
        classes(StandortverwaltungStyles.input)
        value(value)
        placeholder("")
        onInput { onChange(it.value) }
    })
}

private fun standortFieldValue(form: All, field: String): String =
    when (field) {
        "standort" -> form.standort
        "strasse" -> form.strasse
        "hausnummer" -> form.hausnummer
        "plz" -> form.plz
        "ort" -> form.ort
        "land" -> form.land
        "telefon" -> form.telefon
        "email" -> form.email
        "webseite" -> form.webseite
        else -> ""
    }

private fun All.toNetStandortForm(fallbackFirmenId: String): All =
    copy(
        id = id.ifBlank { firmenId.ifBlank { fallbackFirmenId } },
        firmenId = firmenId.ifBlank { id.ifBlank { fallbackFirmenId } },
        standort = standort.ifBlank { firma.ifBlank { kunde } },
        land = land.ifBlank { "DE" }
    )

private fun newStandort(firmenId: String): All =
    All(
        id = firmenId,
        firmenId = firmenId,
        land = "DE"
    )

private object StandortverwaltungStyles : StyleSheet() {
    val page by style {
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
        boxSizing("border-box")
        padding(18.px, 22.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Stretch)
        color(Color.white)
        overflow("hidden")
    }
    val shell by style {
        width(100.percent)
        maxWidth(1320.px)
        minHeight(0.px)
        property("margin", "0 auto")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
    }
    val topbar by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        alignItems(AlignItems.Center)
        flexWrap(FlexWrap.Nowrap)
        gap(12.px)
        padding(18.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
    }
    val header by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.FlexStart)
        gap(10.px)
        minWidth(0.px)
        flexShrink(0)
    }
    val iconBox by style {
        width(72.px)
        height(72.px)
        borderRadius(6.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        backgroundColor(rgba(255, 255, 255, 0.07))
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        boxSizing("border-box")
    }
    val icon by style {
        width(38.px)
        height(38.px)
        property("filter", "brightness(0) invert(1)")
        opacity(0.88)
    }
    val headerText by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.FlexStart)
        gap(5.px)
        minWidth(0.px)
    }
    val titleRow by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(10.px)
        minWidth(0.px)
        flexWrap(FlexWrap.Wrap)
    }
    val title by style {
        margin(0.px)
        fontSize(20.px)
        fontWeight("900")
    }
    val networkPosition by style {
        color(rgba(255, 255, 255, 0.82))
        fontSize(17.px)
        fontWeight("850")
    }
    val actions by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.FlexEnd)
        gap(8.px)
        flexWrap(FlexWrap.Nowrap)
        property("flex", "1 1 auto")
        minWidth(0.px)
    }
    val message by style {
        color(UISettings.ACCENT)
        fontSize(12.px)
        fontWeight("700")
    }
    val content by style {
        minHeight(0.px)
        overflowY("auto")
        paddingRight(4.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(22.px)
    }
    val surface by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
    }
    val sectionBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(18.px)
        padding(22.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.13))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
        boxSizing("border-box")
    }
    val sectionTitle by style {
        marginTop(0.px)
        marginBottom(8.px)
        color(rgba(255, 255, 255, 0.88))
        fontSize(17.px)
        fontWeight("900")
        textAlign("center")
    }
    val groupBlock by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(14.px)
        minWidth(0.px)
        minHeight(0.px)
        paddingBottom(24.px)
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
        self + ":last-child" style {
            paddingBottom(0.px)
            property("border-bottom", "0")
        }
    }
    val groupTitle by style {
        color(rgba(255, 255, 255, 0.78))
        fontSize(13.px)
        fontWeight("800")
    }
    val fieldGrid by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        gap(14.px)
        media(mediaMaxWidth(980.px)) {
            self style {
                property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
            }
        }
        media(mediaMaxWidth(640.px)) {
            self style {
                property("grid-template-columns", "1fr")
            }
        }
    }
    val fieldRow by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(5.px)
    }
    val fieldLabel by style {
        color(rgba(255, 255, 255, 0.62))
        fontSize(10.px)
        fontWeight("800")
        property("text-transform", "uppercase")
        letterSpacing(0.px)
    }
    val input by style {
        height(34.px)
        width(100.percent)
        boxSizing("border-box")
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        borderRadius(5.px)
        backgroundColor(rgba(255, 255, 255, 0.06))
        color(Color.white)
        padding(0.px, 10.px)
        fontSize(12.px)
        property("outline", "none")
        self + ":focus" style {
            property("border-color", UISettings.ACCENT.toString())
            backgroundColor(rgba(255, 255, 255, 0.09))
        }
    }
}
